<script setup lang="ts">
  import { computed, ref } from 'vue';
  import type { DestinationPrices } from '@/entities/searchCriteria';
  import type { SearchFormGoal, SearchFormTracking } from '@/entities/searchForm';
  import type { AccountUser } from '@/features/account';
  import type { SwitchCity } from '@/features/switchCity';
  import {
    SeoLayout,
    useSerpCriteria,
    type SeoDeparture,
    type SeoDepartureCity,
    type SeoMenuItem,
    type SeoTab,
    type SeoTitleTag,
  } from '@/layouts/serp';
  import { STAY_CALENDAR_AB_TEST } from '@/shared/config/abTests';
  import { abTests } from '@/shared/lib/AbTests';
  import {
    HOTEL_SEARCH_FORM_TEXTS,
    SearchForm,
    type SearchFormCriteria,
    type SearchFormExpose,
  } from '@/widgets/searchForm';
  import type { SeoVertical } from '../model/types';

  // Повторяет SeoSearchPageProps: compiler-sfc Vue 2.7 не резолвит импортированный тип в defineProps.
  interface SeoSearchProps {
    modelValue?: SearchFormCriteria | null;
    user?: AccountUser | null;
    accountUrl?: string;
    prices?: DestinationPrices;
    isVisibleRoomButton?: boolean;
    departureDisabled?: boolean;
    destinationDisabled?: boolean;
    cities?: SwitchCity[];
    currentCity?: SwitchCity | null;
    title?: string;
    titleTag?: SeoTitleTag;
    subtitle?: string;
    logo?: string;
    stars?: number;
    menu?: SeoMenuItem[];
    departure?: SeoDeparture | null;
    tabs?: SeoTab[];
    vertical?: SeoVertical;
    isVisibleForm?: boolean;
  }

  const props = withDefaults(defineProps<SeoSearchProps>(), {
    modelValue: null,
    isVisibleRoomButton: false,
    cities: () => [],
    currentCity: null,
    title: '',
    titleTag: 'h1',
    subtitle: '',
    logo: '',
    stars: 0,
    menu: () => [],
    departure: null,
    tabs: () => [],
    vertical: 'tours',
    isVisibleForm: true,
  });

  interface SeoSearchEmits {
    (e: 'update:modelValue', criteria: SearchFormCriteria): void;
    (e: 'search', criteria: SearchFormCriteria): void;
    (e: 'track', goal: SearchFormGoal): void;
    (e: 'login'): void;
    (e: 'logout'): void;
    (e: 'buy'): void;
    (e: 'change:city', city: SwitchCity): void;
    (e: 'change:departure', city: SeoDepartureCity): void;
  }
  const emit = defineEmits<SeoSearchEmits>();

  const HOTEL_TRACKING: SearchFormTracking = { pageType: 'serp', productType: 'hotel' };

  // У отельной нет «Откуда» и многокомнатности — с хоста не включить.
  const verticalForm = computed(() =>
    props.vertical === 'hotels'
      ? { ...HOTEL_SEARCH_FORM_TEXTS, isVisibleDeparture: false, isVisibleRoomButton: false, tracking: HOTEL_TRACKING }
      : { isVisibleRoomButton: props.isVisibleRoomButton },
  );

  const searchFormRef = ref<SearchFormExpose | null>(null);
  const isStayCalendar = computed(() => props.vertical === 'hotels' && abTests.hasTest(STAY_CALENDAR_AB_TEST));
  const criteria = useSerpCriteria(props, emit, isStayCalendar);

  defineExpose<SearchFormExpose>({
    validate: () => (searchFormRef.value ? searchFormRef.value.validate() : true),
    submit: () => searchFormRef.value?.submit(),
    reset: () => searchFormRef.value?.reset(),
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <SeoLayout
    :user="user"
    :account-url="accountUrl"
    :cities="cities"
    :current-city="currentCity"
    :title="title"
    :title-tag="titleTag"
    :subtitle="subtitle"
    :logo="logo"
    :stars="stars"
    :menu="menu"
    :departure="departure"
    :tabs="tabs"
    @login="emit('login')"
    @logout="emit('logout')"
    @buy="emit('buy')"
    @change:city="emit('change:city', $event)"
    @change:departure="emit('change:departure', $event)"
  >
    <template
      v-if="isVisibleForm"
      #form="{ collapsed }"
    >
      <!-- Словари не задаём: у СЕО-страницы свой слаг, он приходит из AppConfig хоста. -->
      <SearchForm
        ref="searchFormRef"
        v-model="criteria"
        v-bind="verticalForm"
        size="l"
        :rounded-bottom="collapsed"
        :prices="prices"
        :departure-disabled="departureDisabled"
        :destination-disabled="destinationDisabled"
        :is-stay-calendar="isStayCalendar"
        @search="emit('search', $event)"
        @track="emit('track', $event)"
      />
    </template>
  </SeoLayout>
</template>
